/**
 * measured-image-swap.js
 * Additive enhancement for the "technical" product template (the measured size
 * picker). When the product has a `dimensions.measurement_image` metafield set,
 * selecting a size swaps the media gallery to that measurement image (e.g. the
 * A/B/C/D dimension diagram) instead of the selected size's variant image.
 *
 * Per-size overrides (added 2026-07-11):
 *   The `dimensions.size_table` JSON may carry a top-level "measurement_media"
 *   object mapping an exact Size value to a product-media id whose diagram
 *   matches that size's actual shape (e.g. snap-clip eye geometry differs
 *   between 20mm and 32mm). The section resolves it into `data-media-map`;
 *   sizes without an entry fall back to the product-wide `data-media-id`.
 *
 * Size-change guard (added 2026-07-11):
 *   The measurement swap fires ONLY when the selected Size value changes.
 *   Pack (or any other option) clicks must not change what's on screen at all.
 *   That takes two parts, because Canopy's `media-gallery.js` scrolls to the
 *   new variant's featured media on EVERY `on:variant:change`:
 *     1. A capture-phase listener records which media is up BEFORE the
 *        gallery's own (bubble-phase, section-level) handler moves it.
 *     2. The bubble-phase handler here (document-level, so it runs after the
 *        gallery's) either swaps to the measurement image (size changed) or
 *        restores the recorded media (size unchanged - e.g. a Pack click),
 *        undoing the gallery's variant-image scroll in the same synchronous
 *        dispatch, so there's no flicker.
 *   `data-size-index` (0-based index of the option named "Size", resolved in
 *   liquid) locates the size in `evt.detail.selectedOptions`; the initially
 *   selected size is read from the picker's inputs at connect time. If the
 *   size can't be determined, the original swap-on-every-change behaviour is
 *   kept.
 *
 * Why this is display-only and feed-safe:
 *   The variant image (clean hero) is left untouched in Shopify, so it remains
 *   each size's `image_link` in the Google Merchant feed. This only re-points
 *   the on-screen carousel to an image that is ALREADY in the gallery — it never
 *   sets a variant's featured media. (See feed-simprosys-gmc-mapping notes.)
 *
 * How it stays correct:
 *   `variant-picker.js` dispatches a bubbling `on:variant:change`. `media-gallery.js`
 *   listens on `.js-product` and scrolls the carousel to the variant's featured
 *   media. We listen on `document`, so our handler runs LATER in the bubble phase
 *   (document is the outermost ancestor) and re-activates the intended slide,
 *   overriding the gallery's scroll. Both calls are synchronous, so there's no
 *   flicker. If the metafield is absent this element is never rendered, so the
 *   default variant-image behaviour is preserved.
 */
if (!customElements.get('measured-image-swap')) {
  class MeasuredImageSwap extends HTMLElement {
    connectedCallback() {
      this.mediaId = this.dataset.mediaId;
      this.section = this.closest('.js-product');
      if (!this.mediaId || !this.section) return;

      this.mediaMap = {};
      if (this.dataset.mediaMap) {
        try {
          this.mediaMap = JSON.parse(this.dataset.mediaMap);
        } catch (e) {
          this.mediaMap = {};
        }
      }
      this.sizeIndex = Number.parseInt(this.dataset.sizeIndex, 10);
      // The script is deferred, so the whole document (including the picker's
      // inputs) is parsed by the time this runs - read the pre-selected size.
      this.lastSize = this.readCurrentSize();
      this.preChangeMediaId = null;

      this.boundCapture = this.captureCurrentMedia.bind(this);
      this.boundHandler = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.boundCapture, true);
      document.addEventListener('on:variant:change', this.boundHandler);
    }

    disconnectedCallback() {
      if (this.boundCapture) {
        document.removeEventListener('on:variant:change', this.boundCapture, true);
      }
      if (this.boundHandler) {
        document.removeEventListener('on:variant:change', this.boundHandler);
      }
    }

    /**
     * Reads the currently selected Size value from the picker's own inputs
     * (mirrors variant-picker.js getSelectedOptions). Null when unknown.
     * @returns {?string} Selected size value or null.
     */
    readCurrentSize() {
      if (Number.isNaN(this.sizeIndex)) return null;
      const picker = this.section.querySelector('variant-picker');
      if (!picker) return null;
      const selectors = picker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      const selector = selectors[this.sizeIndex];
      if (!selector) return null;
      if (selector.dataset.selectorType === 'dropdown') {
        const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
        return selected && selected.dataset.value !== '' ? selected.dataset.value : null;
      }
      const checked = selector.querySelector('input:checked');
      return checked ? checked.value : null;
    }

    /**
     * Returns the Size value carried by the event, or null when it can't be
     * determined (no data-size-index, or the event carries no options).
     * @param {object} evt - The 'on:variant:change' event.
     * @returns {?string} Selected size value or null.
     */
    getSelectedSize(evt) {
      const options = evt.detail && evt.detail.selectedOptions;
      if (!Array.isArray(options) || Number.isNaN(this.sizeIndex)) return null;
      const size = options[this.sizeIndex];
      return typeof size === 'string' ? size : null;
    }

    /**
     * Capture phase: runs before media-gallery's section-level handler, so we
     * can record which media the customer is actually looking at right now.
     * @param {object} evt - The 'on:variant:change' event.
     */
    captureCurrentMedia(evt) {
      if (!this.section.contains(evt.target)) return;
      const gallery = this.section.querySelector('media-gallery');
      this.preChangeMediaId = gallery && gallery.currentItem
        ? gallery.currentItem.dataset.mediaId
        : null;
    }

    handleVariantChange(evt) {
      // Only react to changes originating within our own product.
      if (!this.section.contains(evt.target)) return;

      const gallery = this.section.querySelector('media-gallery');
      if (!gallery || typeof gallery.customSetActiveMedia !== 'function') return;

      const size = this.getSelectedSize(evt);

      if (size !== null && size === this.lastSize) {
        // Pack (or other non-size) change: the gallery has just scrolled to the
        // new variant's featured image - put back whatever was up before.
        if (this.preChangeMediaId) {
          const prev = gallery.querySelector(
            `.media-viewer__item[data-media-id="${this.preChangeMediaId}"]`
          );
          if (prev) gallery.customSetActiveMedia(prev, true);
        }
        return;
      }
      this.lastSize = size;

      // Size changed: show the measurement image.
      // Per-size override (shape-true diagram), else the product-wide default.
      const targetId = (size && this.mediaMap[size]) || this.mediaId;
      let item = gallery.querySelector(
        `.media-viewer__item[data-media-id="${targetId}"]`
      );
      if (!item && `${targetId}` !== `${this.mediaId}`) {
        item = gallery.querySelector(
          `.media-viewer__item[data-media-id="${this.mediaId}"]`
        );
      }
      if (item) gallery.customSetActiveMedia(item, true);
    }
  }

  customElements.define('measured-image-swap', MeasuredImageSwap);
}
